import React, { useState } from "react";
import axios from "axios";
import { Send, Mail, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, required, ...props }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[12px] tracking-[0.08em] text-[#1A1A1A]/60">
      {label}{required && <span className="text-[#b91c1c]"> *</span>}
    </label>
    <input
      required={required}
      {...props}
      className="w-full border border-[#1A1A1A]/25 rounded-sm px-4 py-3 text-[14px] bg-white outline-none focus:border-[#1A1A1A] transition-colors"
    />
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", order_number: "", subject: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", order_number: "", subject: "", message: "" });
      toast.success("Message sent! Our team will get back to you shortly.");
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-[#F4EFE8]">
      {/* HERO */}
      <section className="bg-[#EDE6DC] py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 text-center">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">We're Here To Help</p>
          <h1 className="font-serif-display text-[42px] md:text-[64px] leading-[1.02] font-medium mb-5">
            Contact Us
          </h1>
          <p className="text-[15px] md:text-[17px] text-[#1A1A1A]/70 max-w-2xl mx-auto leading-relaxed">
            Questions about your order, your routine, or red light therapy? Our dermatologist-backed team is happy to help. Fill out the form below and we'll be in touch shortly.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-12">
          {/* Info column */}
          <div>
            <h2 className="font-serif-display text-[28px] md:text-[34px] font-medium mb-6">Get in touch</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#6D28D9]/12 text-[#6D28D9] flex items-center justify-center flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-[14px] font-medium">Email us directly</p>
                  <a href="mailto:team@flowlux.co" className="text-[14px] text-[#6D28D9] underline underline-offset-4">team@flowlux.co</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#6D28D9]/12 text-[#6D28D9] flex items-center justify-center flex-shrink-0">
                  <Clock size={18} />
                </div>
                <div>
                  <p className="text-[14px] font-medium">Response time</p>
                  <p className="text-[14px] text-[#1A1A1A]/60">Within 24 hours, Mon–Fri</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 rounded-full bg-[#6D28D9]/12 text-[#6D28D9] flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <p className="text-[14px] font-medium">30-Day Guarantee</p>
                  <p className="text-[14px] text-[#1A1A1A]/60">Not glowing? We'll make it right.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#EDE6DC] rounded-md p-6 md:p-9">
            {sent ? (
              <div className="text-center py-14">
                <div className="h-14 w-14 rounded-full bg-[#6D28D9]/15 text-[#6D28D9] flex items-center justify-center mx-auto mb-5">
                  <Send size={22} />
                </div>
                <h3 className="font-serif-display text-[28px] font-medium mb-3">Thank you!</h3>
                <p className="text-[14px] text-[#1A1A1A]/70 max-w-sm mx-auto">
                  Your message is on its way to our team. We'll get back to you within 24 hours.
                </p>
                <button onClick={() => setSent(false)} className="btn-lux mt-7 px-8 py-3 text-[12px] tracking-[0.2em] uppercase">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" required placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                  <Field label="Email" required type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone number" required type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
                  <Field label="Order number" placeholder="FL-123456 (if applicable)" value={form.order_number} onChange={set("order_number")} />
                </div>
                <Field label="Subject" placeholder="What's this about?" value={form.subject} onChange={set("subject")} />
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] tracking-[0.08em] text-[#1A1A1A]/60">
                    Questions or concerns<span className="text-[#b91c1c]"> *</span>
                  </label>
                  <textarea
                    rows={5}
                    required
                    placeholder="Tell us how we can help..."
                    value={form.message}
                    onChange={set("message")}
                    className="w-full border border-[#1A1A1A]/25 rounded-sm px-4 py-3 text-[14px] bg-white outline-none focus:border-[#1A1A1A] transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-lux w-full py-4 text-[12px] tracking-[0.22em] uppercase gap-2 disabled:opacity-60"
                >
                  {sending ? "Sending..." : <>Send Message <Send size={14} /></>}
                </button>
                <p className="text-[11px] text-[#1A1A1A]/45 text-center">
                  Your details go straight to our team at team@flowlux.co
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
